import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
  Typography,
} from '@mui/material';
import { PictureAsPdf, VisibilityOutlined } from '@mui/icons-material';
import { recipeV2Client, RecipeV2, RecipeV2Detail } from '../../../app/services/api/recipeV2';
import { loadGovernedPublishingRecipes } from '../publishing/loadPublishingRecipes';
import {
  RecipePublishStyle,
  saveAllergenMatrixPdf,
  saveRecipeBookPdf,
} from '../publishing/recipePublishing';
import GovernedMatrixPreview from './GovernedMatrixPreview';

interface GenerateRecipeBookDialogProps {
  open: boolean;
  onClose: () => void;
}

const BANK_PAGE_SIZE = 100;

const loadAllBankRecipes = async (): Promise<RecipeV2[]> => {
  const recipes: RecipeV2[] = [];
  let page = 1;
  let totalCount = Infinity;
  while (recipes.length < totalCount) {
    const response = await recipeV2Client.getRecipeBankList('', '', [], page, BANK_PAGE_SIZE);
    recipes.push(...response.data);
    totalCount = response.totalCount;
    if (response.data.length === 0) break;
    page += 1;
  }
  return recipes;
};

const GenerateRecipeBookDialog = ({ open, onClose }: GenerateRecipeBookDialogProps) => {
  const { t } = useTranslation('recipeV2');
  const [recipes, setRecipes] = useState<RecipeV2[]>([]);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [style, setStyle] = useState<RecipePublishStyle>('print');
  const [output, setOutput] = useState<'book' | 'matrix'>('book');
  const [matrixPreview, setMatrixPreview] = useState<RecipeV2Detail[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isPreviewLoading, setIsPreviewLoading] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) {
      setRecipes([]);
      setSelectedIds(new Set());
      setMatrixPreview([]);
      return;
    }

    let active = true;
    setIsLoading(true);
    setError(null);
    loadAllBankRecipes()
      .then((items) => {
        if (!active) return;
        setRecipes(items);
        setSelectedIds(new Set(items.map((recipe) => recipe.id)));
      })
      .catch((cause) => {
        if (active) setError(cause instanceof Error ? cause.message : 'Failed to load recipes');
      })
      .finally(() => { if (active) setIsLoading(false); });

    return () => { active = false; };
  }, [open]);

  const selectedRecipes = useMemo(
    () => recipes.filter((recipe) => selectedIds.has(recipe.id)),
    [recipes, selectedIds],
  );

  const clearPreview = () => setMatrixPreview([]);

  const toggleRecipe = (recipeId: number) => {
    clearPreview();
    setSelectedIds((current) => {
      const next = new Set(current);
      if (next.has(recipeId)) next.delete(recipeId);
      else next.add(recipeId);
      return next;
    });
  };

  const toggleAll = () => {
    clearPreview();
    setSelectedIds((current) =>
      current.size === recipes.length ? new Set() : new Set(recipes.map((recipe) => recipe.id)));
  };

  const loadSelectedGovernedRecipes = () =>
    loadGovernedPublishingRecipes(selectedRecipes.map((recipe) => recipe.id));

  const handlePreviewMatrix = async () => {
    if (!selectedRecipes.length) return;
    setIsPreviewLoading(true);
    setError(null);
    try {
      setMatrixPreview(await loadSelectedGovernedRecipes());
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : t('publishing.loadError', 'Unable to load the governed recipes.'));
    } finally {
      setIsPreviewLoading(false);
    }
  };

  const handleGenerate = async () => {
    if (!selectedRecipes.length) return;
    setIsGenerating(true);
    setError(null);
    try {
      const governedRecipes = matrixPreview.length
        ? matrixPreview
        : await loadSelectedGovernedRecipes();
      if (output === 'matrix') {
        saveAllergenMatrixPdf(governedRecipes, { style });
      } else {
        await saveRecipeBookPdf(governedRecipes, { style });
      }
      onClose();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : t('publishing.error', 'Unable to generate the PDF.'));
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <Dialog open={open}
      onClose={isGenerating ? undefined : onClose}
      maxWidth={output === 'matrix' && matrixPreview.length ? 'xl' : 'md'}
      fullWidth>
      <DialogTitle>{t('recipeBank.book.title', 'Publish recipes')}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={3}>
          <Typography color="text.secondary">
            {t('publishing.bookDescription', 'Choose the governed recipes to include, the output type and the presentation style. Commercial costing is excluded from these standard publishing profiles.')}
          </Typography>

          <Box>
            <Typography variant="textSM"
              fontWeight={600}
              sx={{ mb: 1 }}>
              {t('publishing.outputType', 'Output type')}
            </Typography>
            <RadioGroup row
              value={output}
              onChange={(event) => {
                setOutput(event.target.value as 'book' | 'matrix');
                clearPreview();
              }}>
              <FormControlLabel value="book"
                control={<Radio />}
                label={t('publishing.recipeBook', 'Recipe book / selected recipe pack')} />
              <FormControlLabel value="matrix"
                control={<Radio />}
                label={t('publishing.allergenMatrix', 'Allergen & dietary matrix')} />
            </RadioGroup>
          </Box>

          <Box>
            <Typography variant="textSM"
              fontWeight={600}
              sx={{ mb: 1 }}>
              {t('publishing.presentation', 'Presentation')}
            </Typography>
            <RadioGroup
              value={style}
              onChange={(event) => setStyle(event.target.value as RecipePublishStyle)}
            >
              <FormControlLabel value="print"
                control={<Radio />}
                label={t('publishing.printFriendly', 'Print-friendly - minimal ink, no decorative imagery')} />
              <FormControlLabel value="full"
                control={<Radio />}
                label={t('publishing.fullColour', 'Full-colour - richer presentation with imagery where available')} />
            </RadioGroup>
          </Box>

          <Divider />

          <Stack direction="row"
            alignItems="center"
            justifyContent="space-between">
            <Typography variant="textSM"
              fontWeight={600}>
              {t('publishing.recipesSelected', '{{selected}} of {{total}} recipes selected', {
                selected: selectedRecipes.length,
                total: recipes.length,
              })}
            </Typography>
            <Button size="md"
              variant="secondary"
              onClick={toggleAll}
              disabled={isLoading || recipes.length === 0}>
              {selectedIds.size === recipes.length
                ? t('publishing.selectNone', 'Select none')
                : t('publishing.selectAll', 'Select all')}
            </Button>
          </Stack>

          {error && <Typography color="error">{error}</Typography>}
          {isLoading ? (
            <Typography color="text.secondary">{t('loading')}</Typography>
          ) : (
            <Stack divider={<Divider />}
              spacing={1}
              sx={{ maxHeight: 320, overflowY: 'auto' }}>
              {recipes.map((recipe) => (
                <FormControlLabel
                  key={recipe.id}
                  control={(
                    <Checkbox
                      checked={selectedIds.has(recipe.id)}
                      onChange={() => toggleRecipe(recipe.id)}
                    />
                  )}
                  label={(
                    <Box>
                      <Typography variant="textSM"
                        fontWeight={600}>{recipe.description}</Typography>
                      <Typography variant="textXS"
                        color="text.secondary">
                        {recipe.itemNumber} | {recipe.primaryCategory} | {recipe.currentVersion ? `v${recipe.currentVersion}` : t('publishing.legacy', 'legacy governed')}
                      </Typography>
                    </Box>
                  )}
                />
              ))}
            </Stack>
          )}

          {output === 'matrix' && (
            <>
              <Stack direction="row"
                justifyContent="flex-end">
                <Button
                  variant="secondary"
                  startIcon={<VisibilityOutlined />}
                  disabled={isLoading || isPreviewLoading || selectedRecipes.length === 0}
                  onClick={() => { void handlePreviewMatrix(); }}
                >
                  {isPreviewLoading
                    ? t('publishing.loadingPreview', 'Loading preview...')
                    : t('publishing.previewMatrix', 'Preview matrix')}
                </Button>
              </Stack>
              {matrixPreview.length > 0 && <GovernedMatrixPreview recipes={matrixPreview} />}
            </>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}
          disabled={isGenerating}>{t('actions.cancel', 'Cancel')}</Button>
        <Button
          variant="primary"
          startIcon={<PictureAsPdf />}
          disabled={isLoading || isGenerating || selectedRecipes.length === 0}
          onClick={() => { void handleGenerate(); }}
        >
          {isGenerating
            ? t('publishing.generating', 'Generating...')
            : t('publishing.downloadPdf', 'Download PDF')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default GenerateRecipeBookDialog;
